import React, { useState, useEffect, useMemo, useRef } from 'react';
import { haptics } from '../lib/haptics';
import {
  Play,
  Square,
  Plus,
  Building2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  Briefcase,
  Clock
} from 'lucide-react';
import {
  format,
  parseISO,
  startOfISOWeek,
  endOfISOWeek,
  getISOWeek,
  startOfMonth,
  isSameWeek
} from 'date-fns';
import { de } from 'date-fns/locale';
import { useConfirm } from '../lib/ConfirmDialog';

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// --- duration formatting ---------------------------------------------------
const fmtDuration = (sec) => {
  if (!sec || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const fmtHHMM = (sec) => {
  if (!sec || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(
    2,
    '0'
  )}`;
};

const fmtHoursDecimal = (sec) => {
  const h = sec / 3600;
  return h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const entrySeconds = (entry, now) => {
  if (!entry.startedAt) return 0;
  const start = parseISO(entry.startedAt).getTime();
  const end = entry.endedAt ? parseISO(entry.endedAt).getTime() : (now ? now.getTime() : Date.now());
  return Math.max(0, Math.floor((end - start) / 1000));
};

const COMPANY_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#22c55e',
  '#06b6d4',
  '#eab308',
  '#ef4444'
];

const TimeTracker = ({ data, updateData }) => {
  const confirm = useConfirm();

  const tt = data.timetracker || { companies: [], entries: [] };
  const companies = useMemo(() => tt.companies || [], [tt.companies]);
  const entries = useMemo(() => tt.entries || [], [tt.entries]);

  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    try {
      return localStorage.getItem('tt_active_company') || 'all';
    } catch {
      return 'all';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('tt_active_company', selectedCompanyId || 'all');
    } catch {
      /* noop */
    }
  }, [selectedCompanyId]);

  // Live ticking clock — updates every second only when there is a running entry.
  const [now, setNow] = useState(() => new Date());
  const runningEntry = useMemo(() => entries.find((e) => !e.endedAt) || null, [entries]);
  useEffect(() => {
    if (!runningEntry) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [runningEntry]);

  // --- mutate helpers -------------------------------------------------------
  const commit = (patch) => {
    updateData({ timetracker: { ...tt, ...patch } });
  };

  // --- timer start/stop -----------------------------------------------------
  const startTimer = () => {
    if (runningEntry) return;
    haptics.impact();
    const companyId = selectedCompanyId === 'all' ? null : selectedCompanyId;
    const newEntry = {
      id: newId(),
      companyId,
      startedAt: new Date().toISOString(),
      endedAt: null,
      title: '',
      notes: ''
    };
    commit({ entries: [newEntry, ...entries] });
  };

  const [stopTarget, setStopTarget] = useState(null); // entry being stopped/edited
  const [stopForm, setStopForm] = useState({ title: '', notes: '', companyId: null });

  const openStopModal = () => {
    if (!runningEntry) return;
    haptics.impact();
    // End it immediately so the entry is safe even if the user navigates away.
    const endedAt = new Date().toISOString();
    const next = entries.map((e) =>
      e.id === runningEntry.id ? { ...e, endedAt } : e
    );
    commit({ entries: next });
    const ended = { ...runningEntry, endedAt };
    setStopTarget(ended);
    setStopForm({
      title: ended.title || '',
      notes: ended.notes || '',
      companyId: ended.companyId || null
    });
  };

  const saveStopForm = () => {
    if (!stopTarget) return;
    const title = stopForm.title.trim();
    const next = entries.map((e) =>
      e.id === stopTarget.id
        ? { ...e, title, notes: stopForm.notes, companyId: stopForm.companyId || null }
        : e
    );
    commit({ entries: next });
    setStopTarget(null);
  };

  const cancelStopForm = () => {
    // The entry has already been ended; just close the dialog.
    setStopTarget(null);
  };

  // --- entry edit / delete --------------------------------------------------
  const editEntry = (entry) => {
    setStopTarget(entry);
    setStopForm({
      title: entry.title || '',
      notes: entry.notes || '',
      companyId: entry.companyId || null
    });
  };

  const deleteEntry = async (entry) => {
    const ok = await confirm({
      title: 'Eintrag löschen?',
      message: entry.title || fmtDuration(entrySeconds(entry, now)),
      danger: true,
      confirmText: 'Löschen'
    });
    if (!ok) return;
    commit({ entries: entries.filter((e) => e.id !== entry.id) });
  };

  // --- companies ------------------------------------------------------------
  const [showCompanyManager, setShowCompanyManager] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    id: null,
    name: '',
    weeklyHours: '',
    color: COMPANY_COLORS[0]
  });

  const resetCompanyForm = () =>
    setCompanyForm({ id: null, name: '', weeklyHours: '', color: COMPANY_COLORS[0] });

  const editCompany = (c) => {
    setCompanyForm({
      id: c.id,
      name: c.name,
      weeklyHours: c.weeklyHours != null ? String(c.weeklyHours) : '',
      color: c.color || COMPANY_COLORS[0]
    });
    setShowCompanyManager(true);
  };

  const saveCompany = () => {
    const name = companyForm.name.trim();
    if (!name) return;
    const weekly = companyForm.weeklyHours === '' ? null : Number(companyForm.weeklyHours);
    if (weekly != null && (Number.isNaN(weekly) || weekly < 0)) return;
    if (companyForm.id) {
      const next = companies.map((c) =>
        c.id === companyForm.id
          ? { ...c, name, weeklyHours: weekly, color: companyForm.color }
          : c
      );
      commit({ companies: next });
    } else {
      const created = {
        id: newId(),
        name,
        weeklyHours: weekly,
        color: companyForm.color,
        createdAt: new Date().toISOString()
      };
      commit({ companies: [...companies, created] });
      setSelectedCompanyId(created.id);
    }
    resetCompanyForm();
  };

  const deleteCompany = async (c) => {
    const linkedCount = entries.filter((e) => e.companyId === c.id).length;
    const ok = await confirm({
      title: `Firma „${c.name}" löschen?`,
      message:
        linkedCount > 0
          ? `${linkedCount} Einträge bleiben erhalten, werden aber als „Ohne Firma" gezeigt.`
          : 'Diese Firma hat keine Einträge.',
      danger: true,
      confirmText: 'Löschen'
    });
    if (!ok) return;
    const nextEntries = entries.map((e) =>
      e.companyId === c.id ? { ...e, companyId: null } : e
    );
    const nextCompanies = companies.filter((x) => x.id !== c.id);
    commit({ companies: nextCompanies, entries: nextEntries });
    if (selectedCompanyId === c.id) setSelectedCompanyId('all');
  };

  // --- scoped + grouped entries --------------------------------------------
  const visibleEntries = useMemo(() => {
    let list = entries.filter((e) => e.endedAt); // history excludes the running one
    if (selectedCompanyId === 'all') return list;
    if (selectedCompanyId === 'none') {
      return list.filter((e) => !e.companyId);
    }
    return list.filter((e) => e.companyId === selectedCompanyId);
  }, [entries, selectedCompanyId]);

  const sortedEntries = useMemo(() => {
    return [...visibleEntries].sort((a, b) => {
      const aT = parseISO(a.startedAt).getTime();
      const bT = parseISO(b.startedAt).getTime();
      return bT - aT;
    });
  }, [visibleEntries]);

  // Group: month -> week -> entries[]
  const grouped = useMemo(() => {
    const months = new Map(); // monthKey -> { date, weeks: Map<weekKey, { start, end, entries }> }
    for (const e of sortedEntries) {
      const d = parseISO(e.startedAt);
      const monthKey = format(d, 'yyyy-MM');
      const weekStart = startOfISOWeek(d);
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      if (!months.has(monthKey)) {
        months.set(monthKey, { date: startOfMonth(d), weeks: new Map() });
      }
      const m = months.get(monthKey);
      if (!m.weeks.has(weekKey)) {
        m.weeks.set(weekKey, {
          start: weekStart,
          end: endOfISOWeek(d),
          weekNum: getISOWeek(d),
          entries: []
        });
      }
      m.weeks.get(weekKey).entries.push(e);
    }
    return Array.from(months.entries()).map(([key, m]) => ({
      key,
      date: m.date,
      weeks: Array.from(m.weeks.entries()).map(([wk, w]) => ({ key: wk, ...w }))
    }));
  }, [sortedEntries]);

  // Foldable weeks state — default: only the current week is open.
  const [openWeeks, setOpenWeeks] = useState(() => new Set());
  // Auto-open current ISO week the first time entries change.
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    if (grouped.length === 0) return;
    const today = new Date();
    const currentWeekKey = format(startOfISOWeek(today), 'yyyy-MM-dd');
    setOpenWeeks(new Set([currentWeekKey]));
    initRef.current = true;
  }, [grouped]);

  const toggleWeek = (key) => {
    setOpenWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- totals ---------------------------------------------------------------
  const sumSeconds = (list, asOfNow) =>
    list.reduce((acc, e) => acc + entrySeconds(e, asOfNow), 0);

  const totalAll = sumSeconds(sortedEntries);

  const thisWeek = useMemo(() => {
    const today = new Date();
    return sortedEntries.filter((e) => isSameWeek(parseISO(e.startedAt), today, { weekStartsOn: 1 }));
  }, [sortedEntries]);
  const thisWeekIncludingRunning = useMemo(() => {
    if (!runningEntry) return thisWeek;
    const today = new Date();
    if (!isSameWeek(parseISO(runningEntry.startedAt), today, { weekStartsOn: 1 })) {
      return thisWeek;
    }
    // include running entry only if it matches the current scope
    const matchesScope =
      selectedCompanyId === 'all' ||
      (selectedCompanyId === 'none' && !runningEntry.companyId) ||
      runningEntry.companyId === selectedCompanyId;
    return matchesScope ? [...thisWeek, runningEntry] : thisWeek;
  }, [thisWeek, runningEntry, selectedCompanyId]);
  const thisWeekSec = sumSeconds(thisWeekIncludingRunning, now);

  const activeCompany =
    selectedCompanyId !== 'all' && selectedCompanyId !== 'none'
      ? companies.find((c) => c.id === selectedCompanyId)
      : null;
  const targetSec = activeCompany?.weeklyHours
    ? Math.round(activeCompany.weeklyHours * 3600)
    : null;
  const progressPct =
    targetSec && targetSec > 0 ? Math.min(100, Math.round((thisWeekSec / targetSec) * 100)) : null;

  const companyById = (id) => companies.find((c) => c.id === id);

  // --- live elapsed for running entry --------------------------------------
  const runningSec = runningEntry ? entrySeconds(runningEntry, now) : 0;

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Tracker</h2>
        </div>
        <button
          onClick={() => {
            resetCompanyForm();
            setShowCompanyManager(true);
          }}
          className="btn-secondary text-sm flex items-center gap-1"
        >
          <Building2 size={16} />
          Firmen
        </button>
      </div>

      {/* Company filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        <CompanyPill
          active={selectedCompanyId === 'all'}
          onClick={() => setSelectedCompanyId('all')}
          color="#6b7280"
        >
          Alle
        </CompanyPill>
        {companies.map((c) => (
          <CompanyPill
            key={c.id}
            active={selectedCompanyId === c.id}
            onClick={() => setSelectedCompanyId(c.id)}
            color={c.color || '#6b7280'}
          >
            {c.name}
          </CompanyPill>
        ))}
        {entries.some((e) => !e.companyId) && (
          <CompanyPill
            active={selectedCompanyId === 'none'}
            onClick={() => setSelectedCompanyId('none')}
            color="#9ca3af"
          >
            Ohne Firma
          </CompanyPill>
        )}
        <button
          onClick={() => {
            resetCompanyForm();
            setShowCompanyManager(true);
          }}
          className="shrink-0 ml-1 px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 flex items-center gap-1"
        >
          <Plus size={14} />
          Firma
        </button>
      </div>

      {/* Big timer card */}
      <div
        className={`card ${
          runningEntry
            ? 'bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/30 dark:to-blue-900/20 border-primary-200 dark:border-primary-800'
            : ''
        }`}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            {runningEntry ? (
              <>
                <p className="text-xs uppercase tracking-wide text-primary-700 dark:text-primary-300 font-medium">
                  Läuft
                </p>
                <div className="text-4xl sm:text-5xl font-bold tabular-nums text-gray-900 dark:text-gray-100">
                  {fmtHHMM(runningSec)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {runningEntry.companyId
                    ? companyById(runningEntry.companyId)?.name || 'Firma gelöscht'
                    : 'Ohne Firma'}{' '}
                  · seit{' '}
                  {format(parseISO(runningEntry.startedAt), 'HH:mm', { locale: de })}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                  Bereit
                </p>
                <div className="text-4xl sm:text-5xl font-bold tabular-nums text-gray-400 dark:text-gray-500">
                  00:00:00
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Tippe auf Start{' '}
                  {selectedCompanyId !== 'all' && selectedCompanyId !== 'none' && activeCompany
                    ? `für ${activeCompany.name}`
                    : ''}
                </p>
              </>
            )}
          </div>
          {runningEntry ? (
            <button
              onClick={openStopModal}
              className="px-6 py-4 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center gap-2 shadow-md"
            >
              <Square size={20} fill="currentColor" />
              Stoppen
            </button>
          ) : (
            <button
              onClick={startTimer}
              className="px-6 py-4 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white font-semibold flex items-center gap-2 shadow-md"
            >
              <Play size={20} fill="currentColor" />
              Starten
            </button>
          )}
        </div>
      </div>

      {/* This-week summary */}
      <div className="card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
              Diese Woche
            </p>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {fmtHoursDecimal(thisWeekSec)} h
              {targetSec ? (
                <span className="text-base font-normal text-gray-500 dark:text-gray-400">
                  {' '}
                  / {fmtHoursDecimal(targetSec)} h
                </span>
              ) : null}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
              Gesamt
            </p>
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-200">
              {fmtHoursDecimal(totalAll)} h
            </div>
          </div>
        </div>
        {progressPct != null && (
          <div className="mt-3">
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  progressPct >= 100
                    ? 'bg-green-500'
                    : progressPct >= 75
                      ? 'bg-primary-500'
                      : 'bg-primary-400'
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {progressPct}% des Wochenziels
            </p>
          </div>
        )}
      </div>

      {/* History */}
      {grouped.length === 0 ? (
        <div className="card text-center py-10 text-gray-500 dark:text-gray-400">
          <Clock className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Noch keine Einträge.</p>
          <p className="text-sm">Drücke „Starten" um die erste Zeit zu erfassen.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((m) => {
            const monthSec = m.weeks.reduce(
              (acc, w) => acc + w.entries.reduce((a, e) => a + entrySeconds(e), 0),
              0
            );
            return (
              <div key={m.key}>
                <div className="flex items-baseline justify-between mb-2 px-1">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {format(m.date, 'MMMM yyyy', { locale: de })}
                  </h3>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {fmtHoursDecimal(monthSec)} h
                  </span>
                </div>
                <div className="space-y-2">
                  {m.weeks.map((w) => {
                    const weekSec = w.entries.reduce((a, e) => a + entrySeconds(e), 0);
                    const isOpen = openWeeks.has(w.key);
                    return (
                      <div
                        key={w.key}
                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleWeek(w.key)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <div className="flex items-center gap-2 text-left">
                            {isOpen ? (
                              <ChevronDown size={16} className="text-gray-400" />
                            ) : (
                              <ChevronRight size={16} className="text-gray-400" />
                            )}
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                KW {w.weekNum}
                                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                  {format(w.start, 'd.', { locale: de })} –{' '}
                                  {format(w.end, 'd. MMM', { locale: de })}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {w.entries.length} Eintr
                                {w.entries.length === 1 ? 'ag' : 'äge'}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                            {fmtHoursDecimal(weekSec)} h
                          </span>
                        </button>
                        {isOpen && (
                          <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                            {w.entries.map((e) => (
                              <EntryRow
                                key={e.id}
                                entry={e}
                                company={companyById(e.companyId)}
                                onEdit={() => editEntry(e)}
                                onDelete={() => deleteEntry(e)}
                                showCompany={selectedCompanyId === 'all'}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stop / edit modal */}
      {stopTarget && (
        <StopModal
          entry={stopTarget}
          form={stopForm}
          setForm={setStopForm}
          companies={companies}
          onSave={saveStopForm}
          onCancel={cancelStopForm}
          onAddCompany={() => {
            resetCompanyForm();
            setShowCompanyManager(true);
          }}
        />
      )}

      {/* Company manager */}
      {showCompanyManager && (
        <CompanyManager
          companies={companies}
          form={companyForm}
          setForm={setCompanyForm}
          onSave={saveCompany}
          onClose={() => {
            setShowCompanyManager(false);
            resetCompanyForm();
          }}
          onEdit={editCompany}
          onDelete={deleteCompany}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const CompanyPill = ({ active, onClick, color, children }) => (
  <button
    onClick={onClick}
    className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      active
        ? 'text-white border-transparent shadow-sm'
        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800'
    }`}
    style={active ? { backgroundColor: color } : { borderLeftWidth: 4, borderLeftColor: color }}
  >
    {children}
  </button>
);

const EntryRow = ({ entry, company, onEdit, onDelete, showCompany }) => {
  const start = parseISO(entry.startedAt);
  const end = entry.endedAt ? parseISO(entry.endedAt) : null;
  const sec = entrySeconds(entry);
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {format(start, 'EEE d. MMM, HH:mm', { locale: de })}
            {end ? ` – ${format(end, 'HH:mm', { locale: de })}` : ''}
          </span>
          {showCompany && company && (
            <span
              className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded"
              style={{ backgroundColor: `${company.color}22`, color: company.color }}
            >
              {company.name}
            </span>
          )}
        </div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5 truncate">
          {entry.title || <span className="italic text-gray-400">Ohne Titel</span>}
        </div>
        {entry.notes && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 whitespace-pre-wrap">
            {entry.notes}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-200">
          {fmtDuration(sec)}
        </span>
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
          title="Bearbeiten"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Löschen"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const StopModal = ({ entry, form, setForm, companies, onSave, onCancel, onAddCompany }) => {
  const start = parseISO(entry.startedAt);
  const end = entry.endedAt ? parseISO(entry.endedAt) : new Date();
  const sec = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (form.title.trim()) onSave();
          }}
        >
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Was hast du gemacht?
              </h3>
              <button
                type="button"
                onClick={onCancel}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {format(start, 'EEEE d. MMM, HH:mm', { locale: de })} –{' '}
              {format(end, 'HH:mm', { locale: de })} · {fmtDuration(sec)}
            </p>

            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titel
            </label>
            <input
              type="text"
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z.B. Kunde XY – Layout"
              className="input w-full mb-3"
            />

            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notizen <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Details, Tickets, Gedanken…"
              className="input w-full resize-none mb-3"
            />

            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Firma
            </label>
            <div className="flex items-center gap-2">
              <select
                value={form.companyId || ''}
                onChange={(e) => setForm({ ...form, companyId: e.target.value || null })}
                className="input flex-1"
              >
                <option value="">Ohne Firma</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onAddCompany}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 flex items-center gap-1"
              >
                <Plus size={14} /> Neu
              </button>
            </div>
          </div>
          <div className="px-5 pb-5 pt-2 flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!form.title.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CompanyManager = ({ companies, form, setForm, onSave, onClose, onEdit, onDelete }) => {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Firmen</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Existing list */}
        {companies.length > 0 && (
          <div className="px-5 pb-3 space-y-1 overflow-y-auto">
            {companies.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50"
              >
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: c.color || '#6b7280' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {c.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {c.weeklyHours != null ? `${c.weeklyHours} h / Woche` : 'Kein Wochenziel'}
                  </div>
                </div>
                <button
                  onClick={() => onEdit(c)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => onDelete(c)}
                  className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          className="border-t border-gray-100 dark:border-gray-800 px-5 py-4 space-y-3 bg-gray-50/50 dark:bg-gray-900/50"
        >
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {form.id ? 'Firma bearbeiten' : 'Neue Firma'}
          </h4>
          <input
            type="text"
            placeholder="Name der Firma"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input w-full"
            autoFocus
            required
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Stunden / Woche"
              value={form.weeklyHours}
              onChange={(e) => setForm({ ...form, weeklyHours: e.target.value })}
              className="input flex-1"
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">h / Woche</span>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Farbe</p>
            <div className="flex flex-wrap gap-2">
              {COMPANY_COLORS.map((col) => (
                <button
                  key={col}
                  type="button"
                  onClick={() => setForm({ ...form, color: col })}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${
                    form.color === col ? 'scale-110 ring-2 ring-offset-2 ring-gray-400' : ''
                  }`}
                  style={{
                    backgroundColor: col,
                    borderColor: form.color === col ? col : 'transparent'
                  }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            {form.id && (
              <button
                type="button"
                onClick={() => setForm({ id: null, name: '', weeklyHours: '', color: COMPANY_COLORS[0] })}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
              >
                Abbrechen
              </button>
            )}
            <button
              type="submit"
              disabled={!form.name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {form.id ? (
                'Speichern'
              ) : (
                <>
                  <Plus size={14} /> Hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeTracker;
